import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useCookies } from 'react-cookie';

import API from '@/api';
import { sortClientsAscendingByDate } from '@/helpers';
import { createSuspenseQueryHook } from '@/helpers/create-query-hook';
import { COOKIE_KEYS } from '@/constants';

const SESSION_QUERY_KEY = 'session';

const useSessionQuery = createSuspenseQueryHook(
  (accessToken: string) => [SESSION_QUERY_KEY, accessToken],
  async accessToken => {
    // if the url has a client id, use that client id
    const url = new URL(window.location.href);
    const clientId = url.pathname.split('/')[2];
    // if clientid , then set the localStorage client id to that client id
    if (!Number.isNaN(Number(clientId))) {
      const existingWorkspace = JSON.parse(
        localStorage.getItem('workspace') || '{}'
      );
      localStorage.setItem(
        'workspace',
        JSON.stringify({
          ...existingWorkspace,
          client: clientId,
        })
      );
    }

    const [userResponse, businessResponse] = await Promise.all([
      API.user.get(),
      API.business.find({ fields: 'billing' }),
    ]);

    const user = userResponse?.data?.[0];
    const userId = user?.id;
    const availableBusinesses = businessResponse?.data;
    const planResponse = businessResponse?.paymentPlans;

    let paymentPlans;
    if (planResponse && Object.keys(planResponse).length) {
      paymentPlans = planResponse;
    }

    // IMPORTANT: Read workspace data AFTER potentially updating it from URL
    // This ensures we get the latest businessId/clientId after business switching
    let savedWorkspace;
    try {
      const freshWorkspaceData = localStorage.getItem('workspace');
      savedWorkspace = freshWorkspaceData
        ? JSON.parse(freshWorkspaceData)
        : null;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[useSessionQuery] Error parsing workspace data:', error);
    }

    const primaryBusiness = businessResponse?.data?.find(
      (b: { access: { isPrimary: boolean } }) => b.access?.isPrimary
    );

    // `businessId` may have been stringified by writers that round-trip the
    // workspace object through URLSearchParams, so compare loosely. If the
    // stored business is not one of ours (deleted, or membership removed),
    // fall back to the primary/first business instead of resolving nothing.
    const savedBusinessId = savedWorkspace?.businessId;
    const business =
      (savedBusinessId != null
        ? availableBusinesses?.find(
            (b: { id: string | number }) =>
              String(b.id) === String(savedBusinessId)
          )
        : undefined) ??
      primaryBusiness ??
      availableBusinesses?.[0];

    const businessId = business?.id;

    if (business && !!userId && !!businessId) {
      const [
        clientsResponse,
        permissionResponse,
        pagesResponse,
        billingResponse,
      ] = await Promise.all([
        API.clients.find({
          businessId,
          fields: 'accounts',
          $limit: 50,
        }),
        API.permission.find({
          userId,
        }),
        API.accounts.find({
          businessId,
          include: ['provider'],
          paginate: false,
        }),
        API.billingInfo.find({ businessId }),
      ]);

      const permissions = permissionResponse;

      const permission = permissions?.find?.(
        (p: { businessId: string }) =>
          String(p.businessId) === String(businessId)
      );

      const clients = clientsResponse.data.map(
        (client: {
          id: string;
          accounts: { provider: { type: string } }[];
        }) => ({
          ...client,
          accounts: client.accounts.map(account => ({
            service: account.provider.type,
            ...account,
          })),
        })
      );

      const pages = pagesResponse;
      const billing = billingResponse;

      // Select client based on localStorage data or url. A stored client id
      // that is not in this business's client list (stale/deleted workspace,
      // or a business id written into the client slot) must NOT resolve to
      // "no client" — that left `/auth/login` on an infinite loader. Fall
      // back to the oldest client, mirroring the legacy `useGetBusiness`
      // effect, and repair the stored value so RootRedirect agrees with us.
      const storedClient = savedWorkspace?.client;
      const matchedClient =
        storedClient != null
          ? clients.find(
              (c: { id: string }) => String(c.id) === String(storedClient)
            )
          : undefined;
      const selectedClient =
        matchedClient ?? sortClientsAscendingByDate(clients)[0];

      // Repair the stored value whenever it disagrees with what we resolved —
      // including clearing a foreign/stale client id when this business has
      // no client at all, so raw readers (RootRedirect) never act on it.
      const resolvedClientId = selectedClient?.id ?? null;
      if (String(resolvedClientId ?? '') !== String(storedClient ?? '')) {
        try {
          localStorage.setItem(
            'workspace',
            JSON.stringify({
              ...(savedWorkspace ?? {}),
              businessId,
              client: resolvedClientId,
            })
          );
        } catch {
          // storage unavailable — the in-memory session is still correct
        }
      }

      return {
        user,
        client: selectedClient
          ? { ...selectedClient, id: String(selectedClient.id) }
          : null,
        business,
        clients,
        permission,
        permissions,
        paymentPlans,
        availableBusinesses,
        businessOptions: availableBusinesses,
        workspaceData: savedWorkspace, // Include workspace data in the response
        pages,
        billing,
      };
    }

    return {
      user,
      client: null,
      business: null,
      clients: [],
      permission: null,
      permissions: [],
      paymentPlans: null,
      availableBusinesses: [],
      businessOptions: [],
      workspaceData: savedWorkspace,
      billing: null,
    };
  }
);

const useSession = () => {
  const [cookieValues] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);
  return useSessionQuery(cookieValues.accessToken);
};

const useBusiness = () => {
  const [cookieValues] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);
  const query = useSessionQuery(cookieValues.accessToken, {
    select: data => data.business as { id: string },
  });
  return query.data;
};

const useClient = () => {
  const [cookieValues] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);

  const query = useSessionQuery(cookieValues.accessToken, {
    select: data => {
      const selectedClient = data.client as { id: string };
      return selectedClient;
    },
  });
  return query.data;
};

/**
 * Invalidate all of the session queries
 * @param options - Configuration options
 * @param options.refetch - If true, refetch queries immediately instead of just invalidating
 * @returns
 */
const useRefreshSession = () => {
  const queryClient = useQueryClient();
  return useCallback(
    async (options?: { refetch?: boolean }) => {
      if (options?.refetch) {
        await queryClient.refetchQueries({
          queryKey: [SESSION_QUERY_KEY],
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: [SESSION_QUERY_KEY],
        });
      }
    },
    [queryClient]
  );
};

export { useBusiness, useClient, useRefreshSession, useSession };
